import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting, State } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { FaxService, IFaxSearchFilters } from 'app/fm/services/fax-service';
import { NavigationInstruction, RouteConfig } from 'aurelia-router';
import { I18N } from 'aurelia-i18n';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { getDocumentIcon, deepClone, isNullOrUndefined } from 'app/shared/lib/utilities';
import { downloadFile, openFileInWindow } from 'app/shared/lib/files';
import { Toaster } from 'app/shared/lib/toaster';
import { DateValue } from 'app/shared/lib/date-time';
import { IDocumentsConfig, INotesConfig } from 'app/core/models/essette-interfaces';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormCancelCustomEvent, FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { validate } from 'app/shared/elements/ess-rule';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { openNotes } from 'app/core/components/notes/notes-grid';
import { IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { NotesReloadEvent } from 'app/core/lib/events';
import { ApiModelIdentifier } from 'app/shared/models/essette-api';
import { ITickEvent, TickHandler, TickService } from 'app/shared/services/event-services';
import { guid } from 'app/shared/lib/guid';
import * as FM from 'app/fm/models/essette-api';

export interface CanceledDates {
    dateStart?: string | null,
    dateEnd?: string | null
}

interface IFaxDocumentState extends FM.FaxDocument {
    isCurrentUser?: boolean,
    // During polling/tracking fax documents, any found documents that are canceled or out of queue will be marked unavailable for actions
    isTrackedUnavailable?: boolean
}

enum FaxPriority {
    Expedited = 'Expedited'
}

@autoinject()
export class FaxQueue extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected faxQueueId: string | null = null;
    // To load necessary fax queue information
    protected faxQueue: FM.FaxQueue | null = null;

    // Queue Auths to Display on Grid
    protected faxDocuments: Array<IFaxDocumentState> = [];

    // Fields for Grid
    private sorting = new Sorting();
    private defaultSorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected showOnlyCanceled: boolean = false;
    protected showOnlyCheckedOutByMe: boolean = false;
    protected selectedCanceledDates: CanceledDates = {};
    protected appliedCanceledDates: CanceledDates = {};

    protected saveNote: FM.FaxNote | null = null;
    protected saveNoteSelectedFaxDocumentId: string | null = null;

    protected openNotes = openNotes;
    // Subscription
    protected openNotesReload: Subscription | null = null

    // Timer to track checked out users
    private lastUpdate?: number | null = null;
    private readonly updateDuration: number;
    private readonly updateUsers: TickHandler;
    protected readonly trackingState: State = new State();

    protected canViewFaxNotes: boolean = false;
    protected canUploadFaxDocument: boolean = false;
    protected canEditFaxDocumentFileName: boolean = false;
    protected id: guid = guid();

    protected editedFaxDocument: IFaxDocumentState = {};

    public documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'fm/faxes',
        targetType: 'FM.FaxDocument',
        addTargetProperty: (document: FM.FaxDocument) => { document.queue = { id: this.faxQueueId } }
    };

    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'fm/faxes',
        targetType: 'FM.FaxDocument',
        moduleCode: 'FM',
        addTargetProperty: this.addFaxDocumentPropertyForNote.bind(this),
        excludeSystem: false
    };

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $addDocumentModal!: IPopup;
    protected $editDocumentModal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly faxService: FaxService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        private readonly ticker: TickService,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();

        // Set up Document Tracking
        this.updateDuration = this.env.config.tracking.updateIntervalSeconds * 1000; // seconds to milliseconds
        this.updateUsers = (e: ITickEvent) => {
            // lastUpdate is first initialized after fax documents load
            if(!this.lastUpdate) return;

            if(e.time > this.lastUpdate + this.updateDuration) {
                if(!this.env.user.isLoggedIn) {
                    // Reset Tracking
                    this.lastUpdate = 0; // trigger update immediately
                }
                else if(!this.trackingState.busy && this.env.isAppVisible()) {
                    this.track();
                }
            }
        };
    }

    protected bind(): void {
        this.ticker.addEventListener('tick', this.updateUsers);
    }

    protected attached(): void {
        this.openNotesReload = this.events.subscribe(NotesReloadEvent, (e: NotesReloadEvent) => {
            this.reload();
        });
    }

    protected async canActivate(params: { queueId: string }): Promise<boolean> {
        await this.accountContext.load();

        if(!params.queueId) { throw new Error('Missing required parameters: queueId'); }

        const faxQueueRouteCode: string = params.queueId ? `fm.queues.fax.${params.queueId}` : '';
        if(!this.accountContext.hasRouteAccess(faxQueueRouteCode)) { return false; }

        return true;
    }

    protected async activate(params: { queueId: string }, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        // Default Sort Group
        this.defaultSorting.groupEntries = [
            {
                reference: 'metadata.locked.byUser.id',
                matchValue: this.env.user.id
            },
            {
                reference: 'priority.description',
                matchValue: FaxPriority.Expedited
            }
        ];

        // Default Cancel Date Filters
        this.setDefaultCancelDateFilters();

        this.resetGridProperties();

        if(!params?.queueId) {
            throw new Error('Missing required parameters: queueId');
        }

        this.faxQueueId = params.queueId;
        await this.reload();

        await this.loadFaxQueueInformation();
        // Set up Page Title
        if(this.faxQueue && routeConfig.navModel) {
            const title = this.i18n.tr('fm:routes.queue.fax.$title', { what: this.faxQueue.$display });
            routeConfig.navModel.setTitle(title);
        }
    }

    protected detached(): void {
        if(this.openNotesReload) {
            this.openNotesReload.dispose();
        }
    }

    protected unbind(): void {
        this.ticker.removeEventListener('tick', this.updateUsers);
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    protected handleDownloadFile(event: MouseEvent, faxDocument: FM.FaxDocument): void {
        if(event.shiftKey || event.ctrlKey || this.shouldView(faxDocument?.document?.mediaType?.fileExtension || '')) {
            const childWindow = window.open("", "", 'top=25, left=' + (screen.width / 4) + ',width=1000,height=' + (screen.height - 150));
            if(childWindow) {
                this.viewDocument(faxDocument, childWindow);
                return;
            }
        }

        this.downloadDocument(faxDocument);
    }

    protected onShowOnlyCanceledChanged(): void {
        if(this.showOnlyCanceled) {
            this.appliedCanceledDates = {};
            this.setDefaultCancelDateFilters();
            this.applyCancelDateFilter();
        }
        else {
            this.reload();
        }
    }

    protected applyCancelDateFilter(): void {
        if(isNullOrUndefined(this.selectedCanceledDates.dateEnd)) {
            this.selectedCanceledDates.dateEnd = this.selectedCanceledDates.dateStart;
        }

        this.appliedCanceledDates = deepClone(this.selectedCanceledDates);
        this.reload();
    }

    // Open Document Actions
    protected async openDocumentActions(faxDocument: IFaxDocumentState): Promise<void> {
        if(!faxDocument?.id) return;

        // Check first if document is in queue before checkout
        await this.loadDocumentsForTracking(<Array<ApiModelIdentifier>>[faxDocument.id], true);
        const openDocument = <IFaxDocumentState>this.faxDocuments.find(d => d.id === faxDocument.id && !d.isTrackedUnavailable);
        if(!openDocument) {
            this.toaster.warn().message(this.i18n.tr('fm:local.warning.noLongerInQueueOrCanceled', { what: faxDocument.document?.$display })).show();
        }
        else if(openDocument.isLocked && !openDocument.isCurrentUser) {
            this.toaster.warn().message(this.i18n.tr('ui:messages.tracking.user_CheckedOut', { what: faxDocument.document?.$display, who: openDocument.metadata?.locked?.byUser?.name?.full })).show();
        }
        else {
            this.router.navigateToRoute('fm.document', { documentId: faxDocument.id });
        }
    }

    // Open Notes Grid modal
    protected async openNotesGrid(faxDocument: IFaxDocumentState): Promise<void> {
        if(!faxDocument?.id) return;

        // Check first if document is in queue or locked before opening notes grid
        await this.loadDocumentsForTracking(<Array<ApiModelIdentifier>>[faxDocument.id], true);
        const foundFaxDocument = <IFaxDocumentState>this.faxDocuments.find(d => d.id === faxDocument.id && !d.isTrackedUnavailable);
        if(!foundFaxDocument) {
            this.toaster.warn().message(this.i18n.tr(!this.showOnlyCanceled ? 'fm:local.warning.noLongerInQueueOrCanceled' : 'fm:local.warning.noLongerInQueue', { what: faxDocument.document?.$display })).show();
            return;
        }
        else if(foundFaxDocument.isLocked && !foundFaxDocument.isCurrentUser) {
            this.toaster.warn().message(this.i18n.tr('ui:messages.tracking.user_CheckedOut', { what: faxDocument.document?.$display, who: foundFaxDocument.metadata?.locked?.byUser?.name?.full })).show();
            return;
        }

        this.saveNoteSelectedFaxDocumentId = faxDocument.id;

        const dialogOptions: IDialogOptions = {
            width: '10',
            height: '11',
            helpTopic: 'fm.fax-notes',
            title: {
                key: 'fm:routes.fax-notes.$title'
            }
        };

        // TODO: Allow options to view inactive and reactivate/deactivate fax notes once api actions and permissions have been set.
        const canEditNotes = !this.showOnlyCanceled && (!faxDocument.isLocked || faxDocument.isCurrentUser); // can edit notes if 1) Show Only Cancelled is false AND 2) document is either unlocked OR current user
        const readonly = this.showOnlyCanceled || (!!faxDocument.isLocked && !faxDocument.isCurrentUser); // readonly if 1) Show Only Cancelled is true OR 2) document is both locked AND not current user
        this.openNotes(faxDocument.id, this.notesConfig, dialogOptions, canEditNotes, false, false, false, readonly);
    }

    // #region Add Document Modal Events
    protected handleDocumentSaved(event: FormSuccessCustomEvent): void {
        this.closeAddDocumentModal(event.detail.form);
        this.reload();
    }

    protected handleUploadCancel(event: FormCancelCustomEvent): void {
        this.closeAddDocumentModal(event.detail.form);
    }
    // #endregion Add Document Modal Events

    // #region Edit Document Modal Events
    protected handleDocumentChanged(event: FormSuccessCustomEvent): void {
        this.closeEditDocumentModal(event.detail.form);
        this.reload();
    }

    protected handleEditCancel(event: FormCancelCustomEvent): void {
        this.closeEditDocumentModal(event.detail.form);
    }
    // #endregion Edit Document Modal Events

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.canUploadFaxDocument = this.accountContext.hasRouteAccess('fm.queues.upload-document');
        this.canViewFaxNotes = this.accountContext.hasRouteAccess('fm.queues.fax.notes');
        this.canEditFaxDocumentFileName = this.accountContext.hasPermission('Core.Document.Duty.EditDocument');
    }

    public async reload(): Promise<void> {
        await this.loadDocuments();
    }

    public resetGridProperties(): void {
        this.showOnlyCanceled = false;
        this.showOnlyCheckedOutByMe = false;
    }

    // Set Default filter date range for canceled dates
    protected setDefaultCancelDateFilters(): void {
        const today = this.env.today;
        this.selectedCanceledDates.dateStart = (new DateValue(today)).add(-30, 'days').toString();
        this.selectedCanceledDates.dateEnd = today;
    }

    // Open Upload Document Popup
    protected uploadDocument(): void {
        this.$addDocumentModal.open();
    }

    // Open Edit Document Popup
    protected async editDocument(faxDocument: FM.FaxDocument): Promise<void> {
        if(!faxDocument?.id) return;

        // Check first if document is in queue before checkout
        await this.loadDocumentsForTracking(<Array<ApiModelIdentifier>>[faxDocument.id], true);
        this.editedFaxDocument = <FM.FaxDocument>this.faxDocuments.find(d => d.id === faxDocument.id && !d.isTrackedUnavailable);
        if(!this.editedFaxDocument) {
            this.toaster.warn().message(this.i18n.tr('fm:local.warning.noLongerInQueueOrCanceled', { what: faxDocument.document?.$display })).show();
            this.editedFaxDocument = {};
        }
        else if(this.editedFaxDocument.isLocked && !this.editedFaxDocument.isCurrentUser) {
            this.toaster.warn().message(this.i18n.tr('ui:messages.tracking.user_CheckedOut', { what: faxDocument.document?.$display, who: this.editedFaxDocument.metadata?.locked?.byUser?.name?.full })).show();
        }
        else {
            await this.checkoutDocument(this.editedFaxDocument.id!).then(() => {
                this.$editDocumentModal.open();
            });
        }
    }

    protected getDocumentIcon(code: string): string | null {
        return getDocumentIcon(code);
    }

    protected shouldView(extension: string): boolean {
        // TODO: this might warrant being configurable in the future
        const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.txt'];
        const extensionCheck = extension || '';
        return allowed.indexOf(extensionCheck) >= 0;
    }

    // #region Download/View Document
    protected async downloadDocument(faxDocument: FM.FaxDocument): Promise<void> {
        if(!faxDocument?.id) { return; }

        try {
            const blob = await this.faxService.downloadFaxFile(faxDocument.id);
            const fileName = faxDocument.document?.file?.name || 'download';

            downloadFile(blob, fileName);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('fm:faxDocument.$short') })).show();
        }
    }

    protected async viewDocument(faxDocument: FM.FaxDocument, childWindow: Window): Promise<void> {
        if(!faxDocument?.id) { return; }

        try {
            const blob = await this.faxService.downloadFaxFile(faxDocument.id);
            openFileInWindow(childWindow, blob);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('fm:faxDocument.$short') })).show();
            childWindow.close();
        }
    }
    // #endregion Download/View Document

    // Fax Document Property for adding Fax Note
    private addFaxDocumentPropertyForNote(note: FM.FaxNote): void {
        note.document = { id: this.saveNoteSelectedFaxDocumentId };
    }

    private closeAddDocumentModal(form: IForm): void {
        this.$addDocumentModal.close();
        if(form.reset) {
            form.reset();
        }
    }

    private async closeEditDocumentModal(form: IForm): Promise<void> {
        this.$editDocumentModal.close();
        if(form.reset) {
            form.reset();
        }
    }

    private async track(): Promise<void> {
        if(this.faxQueueId && this.faxDocuments?.length) {
            const faxDocumentIds = this.faxDocuments.map(faxDocument => faxDocument.id);

            if(faxDocumentIds?.length) {
                await this.loadDocumentsForTracking(<Array<ApiModelIdentifier>>faxDocumentIds);
            }
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadDocuments(): Promise<void> {
        if(!this.faxQueueId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: this.showOnlyCanceled,
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting.entries.length > 0 ? this.sorting : this.defaultSorting
        };

        const faxFilters: IFaxSearchFilters = {
            excludeCanceled: !this.showOnlyCanceled,
            canceledOnly: this.showOnlyCanceled,
            canceledDateStart: this.appliedCanceledDates.dateStart,
            canceledDateEnd: this.appliedCanceledDates.dateEnd,
            checkoutUserId: this.showOnlyCheckedOutByMe ? this.env.user.id : null
        };

        const includes = [
            'document',
            'document.mediaType',
            'metadata.locked.byUser',
            'notes',
            'priority',
            'source'
        ];

        const fields = [
            'details.pageCount',
            'document.description',
            'document.file.name',
            'document.mediaType.code',
            'from.faxNumber',
            'isLocked',
            'metadata.locked.byUser.name.full',
            'priority.description',
            'queue.id',
            'receivedTimestamp',
            'source.description'
        ];

        try {
            this.state.beginLoad();

            const response = await this.faxService.loadFaxesByQueue(this.faxQueueId, options, includes, faxFilters, fields);
            this.faxDocuments = response.results;

            // Check if locked document belongs to the current user to allow access
            this.faxDocuments.forEach(faxDocument => {
                if(faxDocument.metadata?.locked) {
                    faxDocument.isCurrentUser = faxDocument.metadata.locked.byUser?.id === this.env.user.id;
                }
            });

            this.lastUpdate = Date.now();

            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadDocumentsForTracking(faxDocumentIds: Array<ApiModelIdentifier>, isNonTrack: boolean = false): Promise<void> {
        if(!this.faxQueueId) return;

        const sorting = new Sorting();
        sorting.entries = [{
            reference: 'id'
        }];
        const options: Partial<IQueryLoadOptions> = {
            includeInactive: true,
            sorting: sorting,
            all: true
        };

        const faxFilters: IFaxSearchFilters = {
            faxDocumentIds: faxDocumentIds
        };

        const includes = [
            'metadata.locked.byUser'
        ];

        const fields = [
            'isActive',
            'isLocked',
            'metadata.closed.timestamp',
            'metadata.locked.byUser.name.full',
            'queue.id'
        ];

        try {
            this.trackingState.beginLoad();

            const response = await this.faxService.loadFaxDocuments(options, includes, fields, faxFilters, true);

            const latestFaxDocuments = response.results;

            // Update Checked Out Users and isLocked Status
            this.faxDocuments.forEach(faxDocument => {
                const latestDoc = latestFaxDocuments.find(doc => faxDocument.id === doc.id);

                if(latestDoc) {
                    const checkedOutUser = latestDoc?.metadata?.locked?.byUser;

                    if(faxDocument.metadata?.locked) {
                        faxDocument.metadata.locked.byUser = checkedOutUser ? deepClone(checkedOutUser) : null;
                        // Check if locked document belongs to the current user to allow access
                        faxDocument.isCurrentUser = faxDocument.metadata.locked.byUser?.id === this.env.user.id;
                    }

                    faxDocument.isLocked = latestDoc?.isLocked;
                    faxDocument.queue = latestDoc?.queue;
                    faxDocument.isActive = latestDoc?.isActive;

                    // Identify fax documents to remove if closed, out of queue, or canceled if showing only active
                    if(faxDocument.id && (faxDocument.queue?.id !== this.faxQueueId || (!this.showOnlyCanceled && !faxDocument.isActive) || !!latestDoc.metadata?.closed?.timestamp)) {
                        faxDocument.isTrackedUnavailable = true;
                    }
                    else {
                        faxDocument.isTrackedUnavailable = false;
                    }
                }
            });

            if(!isNonTrack) {
                this.lastUpdate = Date.now();
            }

        } catch(error) {
            throw error;
        } finally {
            this.trackingState.endLoad();
        }
    }

    @apiCall
    private async loadFaxQueueInformation(): Promise<void> {
        if(!this.faxQueueId) return;

        const fields = [
            'description'
        ];

        try {
            this.state.beginLoad();

            const response = await this.faxService.loadQueue(this.faxQueueId, undefined, fields);
            this.faxQueue = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async cancelFaxDocument(faxDocument: IFaxDocumentState): Promise<void> {
        if(!faxDocument.id) return;

        // Check first if document is in queue or locked before canceling
        await this.loadDocumentsForTracking(<Array<ApiModelIdentifier>>[faxDocument.id], true);
        const foundFaxDocument = <IFaxDocumentState>this.faxDocuments.find(d => d.id === faxDocument.id && !d.isTrackedUnavailable);
        if(!foundFaxDocument) {
            this.toaster.warn().message(this.i18n.tr('fm:local.warning.noLongerInQueueOrCanceled', { what: faxDocument.document?.$display })).show();
            return;
        }
        else if(foundFaxDocument.isLocked && !foundFaxDocument.isCurrentUser) {
            this.toaster.warn().message(this.i18n.tr('ui:messages.tracking.user_CheckedOut', { what: faxDocument.document?.$display, who: foundFaxDocument.metadata?.locked?.byUser?.name?.full })).show();
            return;
        }

        // Confirmation Dialog for canceling
        const result = await confirmation.showCancel('fm:faxDocument.$short', 1, false);
        if(!(result.dialogResponse === confirmation.options.OK)) return;

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.faxService.cancelFaxDocument(faxDocument.id);
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.canceled('fm:faxDocument.$short', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async activateFaxDocument(faxDocument: IFaxDocumentState): Promise<void> {
        if(!faxDocument.id) return;

        // Check first if document is in queue or locked before activating
        await this.loadDocumentsForTracking(<Array<ApiModelIdentifier>>[faxDocument.id], true);
        const foundFaxDocument = <IFaxDocumentState>this.faxDocuments.find(d => d.id === faxDocument.id && !d.isTrackedUnavailable);
        if(!foundFaxDocument) {
            this.toaster.warn().message(this.i18n.tr('fm:local.warning.noLongerInQueue', { what: faxDocument.document?.$display })).show();
            return;
        }
        else if(foundFaxDocument.isLocked && !foundFaxDocument.isCurrentUser) {
            this.toaster.warn().message(this.i18n.tr('ui:messages.tracking.user_CheckedOut', { what: faxDocument.document?.$display, who: foundFaxDocument.metadata?.locked?.byUser?.name?.full })).show();
            return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.faxService.activateFaxDocument(faxDocument.id);
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('fm:faxDocument.$short', true, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    protected async checkoutDocument(documentId: string): Promise<boolean> {
        const response = await this.faxService.checkOutFaxDocument(documentId);
        return response.isSuccessful;
    }

}
